/**
 * TNE 2026 — © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/components/system/FeatureGate.tsx
 *
 * Rôle :
 *   Garde GÉNÉRIQUE d'activation/désactivation par feature flag
 *   (Chantier B — demande Mika : « pouvoir couper la page Partenaires
 *   depuis le backoffice »). S'utilise de deux façons :
 *
 *     - en LAYOUT ROUTE (rend <Outlet/>) :
 *         <Route element={<FeatureGate flag="partners.section" />}>
 *           <Route path="/partenaires" element={<PartenairesPage/>} />
 *         </Route>
 *
 *     - en WRAPPER de composant :
 *         <FeatureGate flag="medias.section"><MediasPage/></FeatureGate>
 *
 *   Sémantique « défaut ON » (CLAUDE.md §13 garde-fou « tous les flags
 *   par défaut activés ; la désactivation est un choix explicite ») :
 *     - chargement → on rend le contenu (pas de flash d'écran coupé) ;
 *     - flag inconnu / API flags HS / V26 pas encore appliquée → on
 *       rend le contenu (JAMAIS d'app vidée par accident) ;
 *     - flag présent ET is_enabled === false → écran SOBRE
 *       « section indisponible » (pas un 404 brutal), avec le message
 *       i18n du flag (disabled_message_key) si défini, sinon générique.
 *
 *   ⚠ Sémantique VOLONTAIREMENT INVERSE de `useFeatureFlag().enabled`
 *   (qui, lui, est fail-safe OFF pour les modules sensibles type push/
 *   scanner/IA). Ici on protège la VISIBILITÉ d'une page : couper une
 *   section doit être un acte explicite du backoffice, jamais un effet
 *   de bord d'un flag absent ou d'une API momentanément indisponible.
 *
 * Réutilisable :
 *   100 %. Aucun couplage TNE.
 *
 * Voir : useFeatureFlag.ts, api/feature-flags.php (V3 + V26),
 *        FeatureFlagsPage.tsx, HANDOFF-2026-05-17-22-45.md §2.B.
 *
 * @author Mika
 * @since  2026-05-17
 */

import type { ReactNode } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import styles from './FeatureGate.module.css';

export interface FeatureGateProps {
  /** Clé du feature flag (ex. `partners.section`, `programme.view`). */
  readonly flag: string;
  /**
   * Contenu protégé. Si absent, le composant rend `<Outlet/>` (usage
   * en layout route).
   */
  readonly children?: ReactNode;
}

/**
 * Écran sobre affiché quand une section est explicitement coupée au
 * backoffice. Identité TNE Vosges, thème clair, message i18n du flag
 * si fourni (sinon générique). Jamais un 404 — l'utilisateur comprend
 * que c'est volontaire et temporaire.
 */
function SectionUnavailable({
  label,
  messageKey,
}: {
  label: string | null;
  messageKey: string | null;
}) {
  return (
    <main className={styles.wrap} role="status" aria-live="polite">
      <div className={styles.card}>
        <span className={styles.icon} aria-hidden="true">
          🔒
        </span>
        <h1 className={styles.title}>
          {label ? (
            <FormattedMessage
              id="featureGate.titleNamed"
              values={{ name: label }}
            />
          ) : (
            <FormattedMessage id="featureGate.title" />
          )}
        </h1>
        <p className={styles.body}>
          {messageKey ? (
            <FormattedMessage
              id={messageKey}
              defaultMessage="Cette section est momentanément indisponible."
            />
          ) : (
            <FormattedMessage id="featureGate.body" />
          )}
        </p>
        <Link to="/" className={styles.homeLink}>
          <FormattedMessage id="featureGate.backHome" />
        </Link>
      </div>
    </main>
  );
}

/**
 * Garde par flag. Voir JSDoc d'en-tête pour la sémantique « défaut ON ».
 */
export function FeatureGate({ flag, children }: FeatureGateProps) {
  const { flag: meta, loading } = useFeatureFlag(flag);

  const content = children ?? <Outlet />;

  // Chargement OU flag inconnu → on rend (jamais de flash / d'app vidée).
  if (loading || meta === null) {
    return <>{content}</>;
  }

  // Désactivation EXPLICITE au backoffice → écran sobre.
  if (meta.is_enabled === false) {
    return (
      <SectionUnavailable
        label={meta.label ?? null}
        messageKey={meta.disabled_message_key}
      />
    );
  }

  return <>{content}</>;
}

export default FeatureGate;

// ──────────────────────────────────────────────────────────────────────────
// Notes Mika :
//  - Le hook useFeatureFlag est fail-safe OFF (enabled=false si inconnu)
//    pour des appels type « activer le scanner ? ». Ici on NE l'utilise
//    PAS via `enabled` mais via `flag` (la métadonnée brute) pour
//    appliquer la sémantique inverse « défaut ON » spécifique au gating
//    de pages — voir CLAUDE.md §13.
//  - Pour rétro-port NOLAS : NOLAS coupe une page en commentant du code
//    + redéploiement. Ce composant + l'endpoint public permettent un
//    kill-switch instantané par page sans rebuild.
// ──────────────────────────────────────────────────────────────────────────
